/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */
import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext,
    getCatalogId
} from '@msdyn365-commerce/core';
import { getCartState } from '@msdyn365-commerce/global-state';
import { ProductSearchCriteria, ProductSearchResult } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
// import { getSimpleProducts, ProductInput } from '@msdyn365-commerce-modules/retail-actions';
import { searchByCriteriaAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';

/**
 * Input class for activeCartWithProducts data action.
 */
export class ActiveCartProductsInput implements IActionInput {
    public getCacheKey = () => 'PhibroProductsWithAttributes';

    public getCacheObjectType = () => 'PhibroProductsWithAttributes';

    public dataCacheType = (): CacheType => 'request';
}

const createInput = (inputData: ICreateActionContext) => {
    return new ActiveCartProductsInput();
};

const resolveEffectiveCatalogId = (ctx: IActionContext): number | undefined => {
    const requestContextAny = ctx.requestContext as any;
    const requestAny = requestContextAny?.request ?? requestContextAny;
    const params = requestAny?.params ?? {};
    const query = requestAny?.query ?? {};
    const rawCatalogFromRoute = params.catalogId ?? query.catalogId;
    const catalogFromRoute = rawCatalogFromRoute !== undefined && rawCatalogFromRoute !== null ? Number(rawCatalogFromRoute) : undefined;

    const configuredCatalogId = ctx.requestContext.apiSettings.catalogId as number | undefined;
    const derivedCatalogId = getCatalogId(requestAny);

    if (catalogFromRoute && Number.isFinite(catalogFromRoute) && catalogFromRoute > 0) {
        return catalogFromRoute;
    }

    if (configuredCatalogId && configuredCatalogId > 0) {
        return configuredCatalogId;
    }

    if (derivedCatalogId && derivedCatalogId > 0) {
        return derivedCatalogId;
    }

    return configuredCatalogId;
};

/**
 * Calls the Retail API and returns a cart object based on the passed GetCartInput.
 * @param input
 * @param ctx
 */
export async function getProductsWithAttributes(input: ActiveCartProductsInput, ctx: IActionContext): Promise<ProductSearchResult[]> {
    // If no cart ID is provided in input, we need to create a cart object
    if (!input) {
        throw new Error('[getActiveCartWithProducts]No valid Input was provided, failing');
    }

    const cartState = await getCartState(ctx);
    const cart = cartState.cart;
    // If there are cart lines, make call to get products
    if (!cartState.hasInvoiceLine && cart && cart.CartLines && cart.CartLines.length > 0) {
        const masterProductIds: number[][] = [];
        const productIds: number[] = [];
        // const products = await getSimpleProducts(
        //     <ProductInput[]>cart.CartLines.map(cartLine => {
        //         if (cartLine.ProductId) {
        //             return new ProductInput(cartLine.ProductId, ctx.requestContext.apiSettings);
        //         }
        //         return undefined;
        //     }).filter(Boolean),
        //     ctx
        // );
        // products.map(product => {
        //     if (product.MasterProductId && product.RecordId !== product.MasterProductId) {
        //         masterProductIds.push([product.MasterProductId, product.RecordId]);
        //         productIds.push(product.MasterProductId);
        //         console.log('if push', productIds.push(product.MasterProductId));
        //     } else {
        //         productIds.push(product.RecordId);
        //         console.log('else push', productIds.push(product.RecordId));
        //     }
        // });

        cart.CartLines.map(product => {
            productIds.push(product?.ProductId!);
        });
        const callerContext = ctx;
        const searchCriteriaInput: ProductSearchCriteria = {};
        const effectiveCatalogId = resolveEffectiveCatalogId(ctx);
        searchCriteriaInput.Context = {
            ChannelId: ctx.requestContext.apiSettings.channelId,
            CatalogId: effectiveCatalogId
        };
        searchCriteriaInput.Ids = productIds;
        searchCriteriaInput.IncludeAttributes = true;
        // eslint-disable-next-line no-console
        console.log('[phibro-get-products-with-attributes] request payload', {
            productIds,
            channelId: searchCriteriaInput.Context.ChannelId,
            catalogId: searchCriteriaInput.Context.CatalogId,
            configuredCatalogId: ctx.requestContext.apiSettings.catalogId,
            includeAttributes: searchCriteriaInput.IncludeAttributes
        });

        let productSearchResWithAtts: ProductSearchResult[] = [];

        try {
            const productsWithAttributes = await searchByCriteriaAsync({ callerContext, queryResultSettings: {} }, searchCriteriaInput);
            // eslint-disable-next-line no-console
            console.log('[phibro-get-products-with-attributes] raw response', productsWithAttributes);
            productSearchResWithAtts = productsWithAttributes.map(product => {
                for (const mp of masterProductIds) {
                    if (mp[0] === product.RecordId) {
                        product.RecordId = mp[1];
                    }
                }
                return product;
            });
            // eslint-disable-next-line no-console
            console.log('[phibro-get-products-with-attributes] normalized response', productSearchResWithAtts);
        } catch (error) {
            // Commerce-friendly logging
            ctx.trace('[phibro-get-products-with-attributes] searchByCriteriaAsync failed');

            // Detailed error output (very helpful during SSR debugging)
            console.error('[phibro-get-products-with-attributes] Error:', error);
            // eslint-disable-next-line no-console
            console.error('[phibro-get-products-with-attributes] failed request payload', {
                productIds,
                channelId: searchCriteriaInput.Context?.ChannelId,
                catalogId: searchCriteriaInput.Context?.CatalogId,
                configuredCatalogId: ctx.requestContext.apiSettings.catalogId,
                includeAttributes: searchCriteriaInput.IncludeAttributes
            });

            // Fallback to empty array to avoid page crash
            productSearchResWithAtts = [];
        }
        return productSearchResWithAtts;
    }

    ctx.trace('[getActiveCartWithProducts]No Products Found in cart');
    return <ProductSearchResult[]>[];
}

export const getActiveCartProductsActionDataAction = createObservableDataAction({
    id: 'phibro-get-products-with-attributes',
    action: <IAction<ProductSearchResult[]>>getProductsWithAttributes,
    input: createInput
});
export default getActiveCartProductsActionDataAction;
